import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export type ToastType = "success" | "info" | "warning" | "error";

interface EffectToastProps {
  message: string;
  type?: ToastType;
  icon?: string;
  duration?: number;
  onClose: () => void;
}

/**
 * EffectToast - UI efektleri için animasyonlu toast notification
 * 
 * Kullanım:
 *   <EffectToast 
 *     message={t('effects.likeSuccess')} 
 *     type="success"
 *     icon="❤️"
 *     duration={2000}
 *     onClose={() => setShowToast(false)}
 *   />
 */
export const EffectToast: React.FC<EffectToastProps> = ({
  message,
  type = "success",
  icon,
  duration = 3000,
  onClose,
}) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(onClose, 300); // Wait for exit animation
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const typeConfig = {
    success: {
      bg: "linear-gradient(135deg, #10b981, #059669)",
      border: "#34d399",
      shadow: "0 4px 12px rgba(16, 185, 129, 0.4)",
    },
    info: {
      bg: "linear-gradient(135deg, #3b82f6, #2563eb)",
      border: "#60a5fa",
      shadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
    },
    warning: {
      bg: "linear-gradient(135deg, #f59e0b, #d97706)",
      border: "#fbbf24",
      shadow: "0 4px 12px rgba(245, 158, 11, 0.4)",
    },
    error: {
      bg: "linear-gradient(135deg, #ef4444, #dc2626)",
      border: "#f87171",
      shadow: "0 4px 12px rgba(239, 68, 68, 0.4)",
    },
  };

  const config = typeConfig[type];

  return (
    <div
      className={isExiting ? "" : "animate-slide-in-up"}
      style={{
        position: "fixed",
        bottom: "80px",
        left: "50%",
        transform: isExiting ? "translate(-50%, 100px)" : "translate(-50%, 0)",
        opacity: isExiting ? 0 : 1,
        transition: isExiting ? "all 0.3s ease-out" : "none",
        zIndex: 9999,
        pointerEvents: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "12px 20px",
          borderRadius: "12px",
          background: config.bg,
          border: `2px solid ${config.border}`,
          boxShadow: config.shadow,
          color: "#fff",
          fontSize: "15px",
          fontWeight: "600",
          minWidth: "200px",
          maxWidth: "400px",
        }}
      >
        {icon && (
          <span style={{ fontSize: "24px", lineHeight: 1 }} role="img">
            {icon}
          </span>
        )}
        <span style={{ flex: 1 }}>{message}</span>
        <button
          onClick={() => {
            setIsExiting(true);
            setTimeout(onClose, 300);
          }}
          style={{
            background: "rgba(255, 255, 255, 0.2)",
            border: "none",
            borderRadius: "50%",
            width: "24px",
            height: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#fff",
            fontSize: "16px",
            lineHeight: 1,
            transition: "background 0.2s",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
};

/**
 * ScorePopup - Like/Star verildiğinde +1 gibi popup gösteren component
 */
interface ScorePopupProps {
  points: number;
  x: number;
  y: number;
  color?: string;
}

export const ScorePopup: React.FC<ScorePopupProps> = ({ points, x, y, color = "#10b981" }) => {
  return (
    <div
      className="animate-score-popup"
      style={{
        position: "fixed",
        left: `${x}px`,
        top: `${y}px`,
        fontSize: "24px",
        fontWeight: "bold",
        color: color,
        textShadow: `0 0 10px ${color}, 0 2px 4px rgba(0,0,0,0.3)`,
        pointerEvents: "none",
        zIndex: 9999,
      }}
    >
      +{points}
    </div>
  );
};

/**
 * TPGainToast - SavWar'da TP kazandığında gösterilen özel toast
 */
interface TPGainToastProps {
  tp: number;
  onClose: () => void;
}

export const TPGainToast: React.FC<TPGainToastProps> = ({ tp, onClose }) => {
  const { t } = useTranslation();

  return (
    <EffectToast
      message={t("effects.tpGained", { tp: tp.toFixed(2) })}
      type="success"
      icon="🗡️"
      duration={2500}
      onClose={onClose}
    />
  );
};
